import React, { useState, useEffect, useCallback } from 'react';
import { leadSourcesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2, RefreshCw, TrendingUp, DollarSign, FileText, Briefcase, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { toast } from 'sonner';

const RANGE_OPTIONS = [
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
  { label: '6 months', value: 180 },
  { label: '1 year', value: 365 },
];

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);

export default function LeadSourceReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(90);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await leadSourcesAPI.report(days);
      setData(res.data);
    } catch { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  }, [days]);

  useEffect(() => { fetch(); }, [fetch]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="lead-source-report">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Lead Source Report</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Revenue attribution by lead source</p>
        </div>
        <div className="flex items-center gap-2">
          {RANGE_OPTIONS.map(opt => (
            <Button
              key={opt.value}
              variant={days === opt.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDays(opt.value)}
              data-testid={`range-${opt.value}`}
            >
              {opt.label}
            </Button>
          ))}
          <Button variant="ghost" size="icon" onClick={fetch} disabled={loading} data-testid="refresh-report">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : data ? (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <SummaryCard icon={FileText} label="Estimates" value={data.totals.estimates} />
            <SummaryCard icon={Briefcase} label="Jobs" value={data.totals.jobs} />
            <SummaryCard icon={DollarSign} label="Revenue (Paid)" value={fmt(data.totals.revenue_paid)} accent="text-emerald-600" />
            <SummaryCard icon={TrendingUp} label="Outstanding" value={fmt(data.totals.revenue_outstanding)} accent="text-amber-600" />
          </div>

          {/* Table */}
          {data.rows.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <p className="text-sm">No lead source data for this period.</p>
              <p className="text-xs mt-1">Assign lead sources to customers to start tracking attribution.</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="lead-report-table">
                  <thead>
                    <tr className="bg-muted/40 border-b">
                      <th className="text-left px-4 py-2.5 font-medium">Source</th>
                      <th className="text-right px-4 py-2.5 font-medium">Estimates</th>
                      <th className="text-right px-4 py-2.5 font-medium">Jobs</th>
                      <th className="text-right px-4 py-2.5 font-medium">Est→Job %</th>
                      <th className="text-right px-4 py-2.5 font-medium">Revenue (Paid)</th>
                      <th className="text-right px-4 py-2.5 font-medium">Outstanding</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((row, i) => (
                      <tr key={row.source_id || 'unattributed'} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-3 font-medium">
                          {row.source_name === 'Unattributed' ? (
                            <span className="text-muted-foreground italic">{row.source_name}</span>
                          ) : row.source_name}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">{row.estimates}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{row.jobs}</td>
                        <td className="px-4 py-3 text-right">
                          <ConversionBadge pct={row.conversion_pct} />
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums font-medium text-emerald-700">
                          {fmt(row.revenue_paid)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-amber-700">
                          {row.revenue_outstanding > 0 ? fmt(row.revenue_outstanding) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-muted/30 font-semibold">
                      <td className="px-4 py-2.5">Total</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{data.totals.estimates}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{data.totals.jobs}</td>
                      <td className="px-4 py-2.5 text-right">—</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-emerald-700">{fmt(data.totals.revenue_paid)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-amber-700">{fmt(data.totals.revenue_outstanding)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="border rounded-lg p-3.5">
      <div className="flex items-center gap-2 text-muted-foreground mb-1">
        <Icon className="w-3.5 h-3.5" />
        <span className="text-xs font-medium">{label}</span>
      </div>
      <p className={`text-lg font-semibold tabular-nums ${accent || ''}`}>{value}</p>
    </div>
  );
}

function ConversionBadge({ pct }) {
  if (pct === 0) return <span className="text-muted-foreground">—</span>;
  const color = pct >= 60 ? 'text-emerald-700 bg-emerald-50' : pct >= 30 ? 'text-amber-700 bg-amber-50' : 'text-red-700 bg-red-50';
  return (
    <Badge variant="outline" className={`${color} border-0 text-xs tabular-nums`}>
      {pct >= 50 ? <ArrowUpRight className="w-3 h-3 mr-0.5" /> : <ArrowDownRight className="w-3 h-3 mr-0.5" />}
      {pct}%
    </Badge>
  );
}
